import { PartnerDetail } from '@telescope/veh-shared-components';
import { ExhibitDetails } from '@telescope/veh-shared-components/dist/lib/models';
import { css } from 'aphrodite/no-important';
import { Abstract, LiveChat, LoadingSpinner, ModalWrapper, TrackSession, VideoModal } from 'components';
import { useScrollToTopOnMount } from 'hooks';
import useAchievements, { CATEGORIES } from 'hooks/useAchievements';
import useSessionVideos from 'hooks/useSessionVideos';
import { isEmpty } from 'lodash';
import { VideoData } from 'models';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from 'redux/store';
import api from 'util/api';
import {
  GAEventAction,
  GAEventCategory,
  GAEventLabel,
  trackGoogleEvent,
  trackResourceClick,
  trackVendorChatButtonClick,
  trackVendorEmailButtonClick,
  trackVendorPageView,
  trackVendorSocialLinkClick,
} from 'util/google-helpers';
import { checkIfTrue } from 'util/helpers';
import vehApi from 'util/veh-api';
import { style } from './style';

interface ExhibitParams {
  partnerName: string;
  exhibitId: string;
}

export const Exhibit: FC = () => {
  useScrollToTopOnMount();

  const cmsData = useSelector((state: RootState) => state.widget.cms.text.partner_detail);
  const userData = useSelector((state: RootState) => state.user.data);

  const liveChatWindowOpen = checkIfTrue(cmsData.settings.live_chat_window_enabled);
  const [showChat, setShowChat] = useState(false);
  const { exhibitId } = useParams<ExhibitParams>();
  const { submitAchievement } = useAchievements();

  const [videoModalData, setVideoModalData] = useState<VideoData>();
  const [abstract, setAbstract] = useState<any | undefined>(undefined);
  const allTechnicalTrackVideos = useSessionVideos('/technical-tracks');

  // Filter only the videos that the current exhibitor is featured in
  const exhibitTechnicalTrackVideos = allTechnicalTrackVideos
    .filter((video) => {
      const exhibitIds = video.exhibit_id.split(',').map((id) => id.trim());
      return exhibitIds.includes(exhibitId);
    })
    .map((video: VideoData, index: number) => (
      <TrackSession
        key={index}
        video={video}
        onAbstractAndBioClick={() => handleAbstractClick(video.abstract_url)}
        onVideoClick={() => handleVideoClick(video)}
      />
    ));

  const [partnerData, setPartnerData] = useState<ExhibitDetails | null>(null);
  const location = useLocation();
  const history = useHistory();
  const styles = style();
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (exhibitId) {
      (async () => {
        try {
          const response = await vehApi.getPublishedExhibit(exhibitId);
          setPartnerData(response);
          trackVendorPageView(response.basic_info.vendor_name, location.pathname);
        } catch (e) {
          console.error('API: Invalid Partner data URL');
          setErrorMessage(cmsData.no_content_text);
        }
      })();

      submitAchievement(CATEGORIES.EXHIBIT, 'EXHIBIT' + exhibitId);
    }
  }, [cmsData.no_content_text, exhibitId, location.pathname, submitAchievement]);

  if (!partnerData) {
    return <LoadingSpinner></LoadingSpinner>;
  }

  const handleAbstractClick = async (url: string) => {
    if (!url) return;
    const response = await api.fetchAbstract(url);
    if (!response) return;
    setAbstract(response);
    trackGoogleEvent(GAEventCategory.SITE, GAEventAction.CLICK, GAEventLabel.SPEAKER_BIO);
  };

  const handleVideoClick = (video: VideoData) => {
    window.scrollTo(0, 0);
    setVideoModalData(video);
  };

  const handleChatButtonClick = () => {
    const vendorName = partnerData.basic_info.vendor_name;
    trackVendorChatButtonClick(vendorName);
    setShowChat(true);
  };

  const handleEmailButtonClick = () => {
    const vendorName = partnerData.basic_info.vendor_name;
    trackVendorEmailButtonClick(vendorName);
  };

  const handleResourceClick = (title: string, url: string) => {
    const vendorName = partnerData.basic_info.vendor_name;
    trackResourceClick(vendorName, title);
  };

  const handleSocialLinkClick = (label: string) => {
    const vendorName = partnerData.basic_info.vendor_name;
    trackVendorSocialLinkClick(vendorName, label);
  };

  const handleBackButtonClick = () => {
    history.push('/partner-pavilion');
  };
  return (
    <>
      <div className={css(styles.exhibitContainer)}>
        {isEmpty(partnerData) ? (
          <main>
            <h2 className={css(styles.displayError)}>{errorMessage}</h2>
          </main>
        ) : (
          <PartnerDetail
            details={partnerData}
            technicalTrackSessions={exhibitTechnicalTrackVideos}
            isLiveChatWindowOpen={liveChatWindowOpen}
            isPreview={false}
            backButtonText={'Back to Partner Pavilion Lobby'}
            onBackButtonClick={handleBackButtonClick}
            onChatButtonClick={handleChatButtonClick}
            onResourceClick={handleResourceClick}
            onEmailButtonClick={handleEmailButtonClick}
            onSocialLinkClick={handleSocialLinkClick}
          />
        )}
        {!isEmpty(partnerData) && partnerData.live_chat.is_participating && (
          <LiveChat
            showChat={showChat}
            userData={userData}
            exhibitData={partnerData}
            onRequestClose={() => setShowChat(false)}></LiveChat>
        )}
      </div>
      {videoModalData && (
        <VideoModal
          active={!!videoModalData}
          video={videoModalData}
          autoplay
          autoplayNext
          onRequestClose={() => setVideoModalData(undefined)}
        />
      )}
      <ModalWrapper
        isOpen={!!abstract}
        options={{ fillSmallerScreens: true, preventScroll: true }}
        onRequestClose={() => setAbstract(undefined)}
        customStyles={{
          modalContent: {
            desktop: { padding: '80px 26px 30px 69px' },
            tablet: { padding: '80px 20px 56px 30px' },
            mobile: { padding: '80px 20px 40px 20px' },
          },
        }}>
        {abstract && (
          <Abstract
            title={abstract?.presentation_title}
            abstract={abstract?.presentation_abstract}
            speakerDetails={abstract?.speakers}
            cmsData={{ speakers: 'Speakers', experience: 'Experience' }}
          />
        )}
      </ModalWrapper>
    </>
  );
};
